import styles from "./styles.module.scss";
import { FC, Props } from "./typings";

import statusService, { ISetStatus } from "api/statusService";
import { useContext, useEffect, useMemo, useState } from "react";
import StatusBadge from "components/StatusBadge";
import { IStatus } from "dto/IStatus";
import Input from "components/Input";
import PackagesContext from "contexts/PackagesContext";
import { ItemKind } from "dto/IKindItems";
import CasesContext from "contexts/CasesContext";
import CaseChatContext from "contexts/CaseChatContext";
import { ENotificationType, notification } from "utils/notification";
import { getCompanyId as getWorkspaceId } from "utils/getCompanyId";
import { getListType } from "utils/getTagType";
import NotFoundScreen from "components/NotFoundScreen";
import { useTranslation } from "react-i18next";

const StatusModal: FC<Props> = ({
  toggle,
  item,
  items,
  selectedStatus,
  afterUpdate,
  listType,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [search, setSearch] = useState<IStatus[]>([]);

  const currentWorkspaceId = getWorkspaceId();
  const { data: statusesData, isLoading } = statusService.useGetStatuses(
    currentWorkspaceId ?? undefined,
    listType ?? getListType(item!.Kind),
  );
  const { mutate: setStatus } = statusService.useSetStatus();
  const { mutate: setManyStatus } = statusService.useSetManyStatus();

  const [selectedFlag, setSelectedFlag] = useState<IStatus | null>(
    selectedStatus,
  );

  const allStatuses: IStatus[] = useMemo(
    () => statusesData?.data ?? [],
    [statusesData],
  );

  const { packages, setPackages } = useContext(PackagesContext);
  const { cases, setCases } = useContext(CasesContext);
  const { casePackages, setCasePackages } = useContext(CaseChatContext);

  useEffect(() => {
    if (!value.length) {
      setSearch(allStatuses);
      return;
    }

    setSearch(
      allStatuses.filter(
        (s: IStatus) =>
          s.Name.toLocaleLowerCase().search(value.toLocaleLowerCase()) !== -1,
      ),
    );
  }, [allStatuses, value]);

  const handleChangeFlag = (flag: IStatus) => {
    setSelectedFlag(flag);
    const newPacks = [...packages];
    const newCases = [...cases];
    const newCasePacks = [...casePackages];
    if (items) {
      setPackages(
        newPacks.map((a) => {
          if (items.find((p) => p.Id === a.Id)) {
            a.StatusId = flag.Id;
            return a;
          }
          return a;
        }),
      );

      const itemsToUpdate: ISetStatus[] = items.map((p) => ({
        OwnerId: p.Id,
        OwnerKind: p.Kind,
        StatusId: flag.Id as number,
      }));

      setManyStatus(
        {
          Items: itemsToUpdate,
          StatusId: flag.Id as number,
        },
        {
          onSuccess: () => {
            if (afterUpdate) {
              afterUpdate(flag);
            }
            toggle(false);
          },
          onError: () => {
            notification({
              type: ENotificationType.ERROR,
              title: t("errors.occurred"),
              text: t("errors.somethingWentWrongRetryLater"),
            });
          },
        },
      );
    } else if (item) {
      const packItem = newPacks.find(
        (p) => p.Id === item.Id && p.Kind === item.Kind,
      );
      const caseItem = newCases.find(
        (c) => c.Id === item.Id && c.Kind === ItemKind.Cases,
      );
      const packCaseItem = newCasePacks.find(
        (p) => p.Id === item.Id && p.Kind === item.Kind,
      );

      if (packItem) {
        packItem.StatusId = flag.Id;
        setPackages(newPacks);
      }

      if (caseItem) {
        caseItem.StatusId = flag.Id;
        setCases(newCases);
      }

      if (packCaseItem) {
        packCaseItem.StatusId = flag.Id;
        setCasePackages(newCasePacks);
      }

      // if (dossierItem) {
      //   dossierItem.StatusId = flag.Id;
      //   setDossierFiles(newDossierFiles);
      // }

      setStatus(
        {
          OwnerId: item.Id,
          OwnerKind: item.Kind,
          StatusId: flag.Id as number,
        },
        {
          onSuccess: () => {
            if (afterUpdate) {
              afterUpdate(flag);
            }

            toggle(false);
          },
          onError: () => {
            notification({
              type: ENotificationType.ERROR,
              title: t("errors.occurred"),
              text: t("errors.somethingWentWrongRetryLater"),
            });
          },
        },
      );
    }
  };

  return (
    <div className={styles.modal}>
      <div className={styles.wrapper}>
        <div className={styles.content}>
          <Input
            focus
            placeholder={t("common.search")}
            onChange={(text) => setValue(text)}
            label={t("common.search")}
          />
          <div className={styles.flagsWrapper}>
            {search.map((status: IStatus) => (
              <div
                key={status.Id}
                onClick={(e) => {
                  handleChangeFlag(status);
                  e.stopPropagation();
                }}
                className={`${styles.flag} ${
                  selectedFlag?.Id === status.Id ? styles.active : ""
                }`}
              >
                <StatusBadge onRemove={() => {}} status={status} />{" "}
                {status.Name}
              </div>
            ))}
            {!isLoading && search.length === 0 && (
              <div className={styles.empty}>
                <NotFoundScreen
                  title={t("emptyStates.notFound")}
                  text={t("emptyStates.noResultsSuffix")}
                  size="normal"
                ></NotFoundScreen>
              </div>
            )}
          </div>
        </div>
        {/* {selectedFlag && (
          <div className={styles.editor}>
            {editMode && item ? (
              <EditMode
                toggle={setEditMode}
                handleCreateStatus={handleCreateStatus}
                handleUpdateStatus={handleUpdateStatus}
                status={!createNew ? selectedFlag : null}
                item={item}
              />
            ) : (
              <PreviewMode
                onClick={() => {
                  setCreateNew(false);
                  setEditMode(true);
                }}
                status={selectedFlag}
              />
            )}
          </div>
        )} */}
        {/* {editMode ? (
          <EditMode
            toggle={setEditMode}
            handleCreateStatus={handleCreateStatus}
            handleUpdateStatus={handleUpdateStatus}
            status={!createNew ? selectedFlag : null}
            item={item ?? undefined}
          />
        ) : (
          <div className={styles.buttonWrapper}>
            <Button
              className={styles.button}
              onClick={() => {
                setCreateNew(true);
                setEditMode(true);
              }}
              text="Dodaj status"
            />
          </div>
        )} */}
      </div>
    </div>
  );
};

export default StatusModal;
